import classnames from 'classnames2';
import { capitalize } from 'lodash-es';
import React, { useState } from 'react';

import { IBaseProps } from '../../utils';

import { Icon } from '../../Icon';
import { InputSizes, InputTypes, ValidationStates } from '../enums';

import styles from './BaseInput.module.scss';

export type OnChangeCallback = (newValue: string) => void;

export interface IBaseInputProps extends IBaseProps {
  /** Appends CSS classes to the container `<input>` element */
  inputClassName?: string;
  size?: 'default' | 'small' | 'large'; // Move towards using size through a prop instead of a namespace
  /** Set to true if you want to disable the input from accepting changes from the user */
  disabled?: boolean;
  /** Id attribute assigned to the input field. Used to associate the input with other form elements (Ex: label) */
  id?: string;
  /** Maximum number of characters the value can have */
  maxLength?: number;
  /** Minimum number of characters the value can have */
  minLength?: number;
  /** Callback function for when input value is changed */
  onChange?: OnChangeCallback;
  /** Number displayed when value is empty */
  placeholder?: string;
  /** Determines if field is required */
  required?: boolean;
  /** Validation state of the input which can be either success or error */
  validationState?: ValidationStates;
  /** The current value of the input that is displayed to the user */
  value?: string | number;
  /** Sets a default value for the input */
  defaultValue?: string | number;
  /** Minimum value the input can have */
  min?: string | number;
  /** Maximum value the input can have */
  max?: string | number;
  step?: string | number;
  name?: string;
  /** Displays the action-enter icon to the right of the input when interacting with it */
  showEnterIcon?: boolean;
  /** Enables the clear input button when a value is present */
  canClearInput?: boolean;
  /** Callback function for when input loses focus */
  onBlur?(event): void;
  /** Callback function for when input is focused */
  onFocus?(event): void;
  /** Callback function for when the input is focused and the user pastes text in */
  onPaste?(event): void;
  /** Callback function for when the input is focused and the user presses down on a key */
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  /** Callback function for when the input is focused and the user presses up on a key */
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void;
}

export type BaseInputComponent = React.FC<IBaseInputProps>;

export const getContainerClasses = (inputSize, validationState, disabled) => {
  return classnames(styles.container, {
    [styles.defaultSize]: inputSize === InputSizes.Default,
    [styles.smallSize]: inputSize === InputSizes.Small,
    [styles.largeSize]: inputSize === InputSizes.Large,
    [styles.successState]: validationState === ValidationStates.Success,
    [styles.errorState]: validationState === ValidationStates.Error,
    [styles.disabledState]: disabled
  });
};

export const preventEnterNonNumericCharacters = (event) => {
  // Allow keyboard shortcuts
  const { key } = event;
  if (
    event.getModifierState &&
    (event.getModifierState('Meta') || event.getModifierState('Control') || event.getModifierState('Alt'))
  ) {
    return;
  }
  // Allow non-printable keys
  if (key.length !== 1 || key === '\x00') {
    return;
  }
  // Prevent any non-numeric keys, but allow "." for decimals and "-" for negative values
  if ((key < '0' || key > '9') && key !== '.' && key !== '-') {
    event.preventDefault();
  }
};

export const preventPasteNonNumericCharacters = (event) => {
  const pasteText = (event.originalEvent || event).clipboardData.getData('text/plain');
  const formattedPasteText = pasteText.replace(/[^\d.-]/g, '');
  if (formattedPasteText === pasteText) return;
  event.preventDefault();
  const { target } = event;
  const { selectionStart = null, selectionEnd = null, value = '' } = target;
  if (selectionStart === null || selectionEnd === null) {
    target.value = formattedPasteText;
  } else {
    const textBefore = value.substring(0, selectionStart);
    const textAfter = value.substring(selectionEnd, value.length);
    target.value = `${textBefore}${formattedPasteText}${textAfter}`;
  }
};

const createBaseInput = (type: InputTypes, size: InputSizes) => {
  const BaseInputElement: BaseInputComponent = (props, ref?: React.Ref<HTMLInputElement>) => {
    const [showEnterIcon, setShowEnterIcon] = useState(false);
    const inputSize = capitalize(props.size) || size;

    const containerStyle = getContainerClasses(inputSize, props.validationState, props.disabled);

    const createChangeHandler = (callback: OnChangeCallback) => callback && (({ target }) => callback(target.value));

    const clearInputHandler = () => {
      props.onChange(null);
      inputProps = Object.assign(inputProps, { value: '' });
    };

    const handleOnBlur = (e) => {
      if (props.showEnterIcon) {
        setShowEnterIcon(false);
      }
      return props.onBlur && createChangeHandler(props.onBlur)(e);
    };

    const handleOnFocus = (e) => {
      if (props.showEnterIcon) setShowEnterIcon(true);
      return props.onFocus && props.onFocus(e);
    };

    const handleOnClick = () => {
      if (props.showEnterIcon) setShowEnterIcon(true);
    };

    const handleOnKeyDown = (e) => {
      if (type === InputTypes.Number) preventEnterNonNumericCharacters(e);
      return props.onKeyDown && props.onKeyDown(e);
    };

    const handleOnPaste = (e) => {
      if (type === InputTypes.Number) preventPasteNonNumericCharacters(e);
      return props.onPaste && props.onPaste(e);
    };

    let inputProps = {};

    if (props.value === null) {
      inputProps = Object.assign(inputProps, { value: '' });
    } else if (props.value !== undefined) {
      inputProps = Object.assign(inputProps, { value: props.value });
    }

    if (props.defaultValue) {
      inputProps = Object.assign(inputProps, { defaultValue: props.defaultValue });
    }

    return (
      <div className={classnames(containerStyle, props.className)}>
        <input
          className={classnames(styles.input, props.inputClassName)}
          data-test={props.dataTest}
          disabled={props.disabled}
          id={props.id}
          maxLength={props.maxLength}
          minLength={props.minLength}
          onBlur={handleOnBlur}
          onChange={createChangeHandler(props.onChange)}
          onFocus={handleOnFocus}
          onKeyDown={handleOnKeyDown}
          onKeyUp={props.onKeyUp}
          onPaste={handleOnPaste}
          onClick={handleOnClick}
          placeholder={props.placeholder}
          ref={ref}
          required={props.required}
          type={type.toLowerCase()}
          min={props.min}
          max={props.max}
          step={props.step}
          name={props.name}
          {...inputProps}
        />
        {props.validationState && (
          <span className={styles.iconWrapper}>
            {props.validationState === ValidationStates.Success && (
              <Icon className={styles.icon} icon="status-success" dataTest="success-icon" />
            )}
            {props.validationState === ValidationStates.Error && (
              <Icon className={styles.icon} icon="status-error" dataTest="error-icon" />
            )}
          </span>
        )}
        {showEnterIcon && (!props.validationState || props.validationState === ValidationStates.None) && (
          <span className={styles.iconWrapper}>
            <Icon className={styles.enterIcon} icon="action-enter" dataTest="enter-icon" />
          </span>
        )}
        {props.canClearInput && props.value && (
          <span className={styles.closeIconWrapper} onClick={clearInputHandler} role="button">
            <Icon icon="action-close" dataTest="close-icon" />
          </span>
        )}
      </div>
    );
  };

  BaseInputElement.displayName = `Input.${type}.${size}`;

  return React.forwardRef(BaseInputElement);
};

export function createBaseInputNamespace(type) {
  return Object.assign(createBaseInput(type, InputSizes.Default), {
    Default: createBaseInput(type, InputSizes.Default),
    Small: createBaseInput(type, InputSizes.Small),
    Large: createBaseInput(type, InputSizes.Large)
  });
}
